import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  PlusIcon,
  FunnelIcon,
  ArrowPathIcon,
  EyeIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { TOKEN_SYMBOL, formatTokenAmount } from '../config/token';
import { usePagination } from '../hooks/usePagination';
import Pagination from './Pagination';
import { walletService } from '../services/walletService';

interface Transaction {
  id: string;
  transactionId: string;
  amount: number;
  type: string;
  status: string;
  signature: string | null;
  metadata: any;
  fromWalletId: string;
  toWalletId: string;
  createdAt: string;
  updatedAt: string;
  errorMessage?: string;
}

interface Filters {
  type: string;
  status: string;
  fromDate: string;
  toDate: string;
  amount: string;
  search: string;
}

const EMPTY_FILTERS: Filters = {
  type: '',
  status: '',
  fromDate: '',
  toDate: '',
  amount: '',
  search: '',
};

const hasActiveFilters = (filters: Filters): boolean =>
  Boolean(
    filters.type ||
      filters.status ||
      filters.fromDate ||
      filters.toDate ||
      filters.amount ||
      filters.search,
  );

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const TransactionList: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [repairing, setRepairing] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [creatingWallet, setCreatingWallet] = useState(false);
  const [newWalletAddress, setNewWalletAddress] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const { page, pageSize, setPage, setPageSize } = usePagination();

  // `filters` is the form state; `appliedFilters` is what actually drives the query.
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(pageSize));

      let url: string;
      if (hasActiveFilters(appliedFilters)) {
        if (appliedFilters.type) params.append('type', appliedFilters.type);
        if (appliedFilters.status) params.append('status', appliedFilters.status);
        if (appliedFilters.fromDate) params.append('fromDate', appliedFilters.fromDate);
        if (appliedFilters.toDate) params.append('toDate', appliedFilters.toDate);
        if (appliedFilters.amount) params.append('amount', appliedFilters.amount);
        if (appliedFilters.search) params.append('search', appliedFilters.search);
        url = `${API_BASE_URL}/transaction/filter/search?${params.toString()}`;
      } else {
        url = `${API_BASE_URL}/transaction?${params.toString()}`;
      }

      const response = await axios.get(url);
      setTransactions(response.data.data);
      setTotalItems(response.data.meta.total);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedFilters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleRetry = async (transactionId: string) => {
    try {
      setRetrying(transactionId);
      await axios.post(`${API_BASE_URL}/transaction/${transactionId}/retry`);
      fetchTransactions();
    } catch (error) {
      console.error('Error retrying transaction:', error);
      alert('Failed to retry transaction');
    } finally {
      setRetrying(null);
    }
  };

  const handleRepairAndRetry = async (transaction: Transaction) => {
    const key = transaction.transactionId;
    try {
      setRepairing(key);
      setRetrying(key);
      await walletService.repairTransactionWallets(transaction.fromWalletId, transaction.toWalletId);
      await axios.post(`${API_BASE_URL}/transaction/${transaction.transactionId}/retry`);
      fetchTransactions();
    } catch (error: any) {
      console.error('Error repairing and retrying transaction:', error);
      alert(error.response?.data?.message || 'Failed to repair and retry transaction');
    } finally {
      setRepairing(null);
      setRetrying(null);
    }
  };

  const createWallet = async () => {
    setCreatingWallet(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/blockchain/create`);
      if (response.data && response.data.accountPublic) {
        setNewWalletAddress(response.data.accountPublic);
        fetchTransactions();
      } else {
        alert('Wallet created but response format unexpected');
      }
    } catch (error: any) {
      console.error('Error creating wallet:', error);
      alert('Failed to create wallet: ' + (error.response?.data?.message || error.message));
    } finally {
      setCreatingWallet(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Wallet address copied to clipboard!');
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-500/15 text-green-300 ring-1 ring-inset ring-green-500/30';
      case 'pending':
        return 'bg-yellow-500/15 text-yellow-300 ring-1 ring-inset ring-yellow-500/30';
      case 'failed':
        return 'bg-red-500/15 text-red-300 ring-1 ring-inset ring-red-500/30';
      default:
        return 'bg-slate-700 text-slate-300 ring-1 ring-inset ring-slate-600';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'send_to_customer':
        return 'bg-blue-500/15 text-blue-300 ring-1 ring-inset ring-blue-500/30';
      case 'transfer_to_merchant':
        return 'bg-purple-500/15 text-purple-300 ring-1 ring-inset ring-purple-500/30';
      case 'withdrawal':
        return 'bg-orange-500/15 text-orange-300 ring-1 ring-inset ring-orange-500/30';
      default:
        return 'bg-slate-700 text-slate-300 ring-1 ring-inset ring-slate-600';
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  const inputClass =
    'mt-1 block w-full rounded-md border-slate-600 bg-slate-700 text-slate-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm';
  const labelClass = 'block text-sm font-medium text-slate-300';

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Transactions</h1>
          <p className="mt-2 text-sm text-slate-400">
            Manage and monitor all {TOKEN_SYMBOL} token transactions
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
          <button
            onClick={createWallet}
            disabled={creatingWallet}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 disabled:opacity-50"
          >
            {creatingWallet ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Wallet
              </>
            )}
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          <Link
            to="/failed-transactions"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-red-500 shadow-sm"
          >
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            Failed Transactions
          </Link>
        </div>
      </div>

      {/* Wallet Created Modal */}
      {newWalletAddress && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-slate-700 w-96 shadow-xl rounded-xl bg-slate-800">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-500/15 rounded-full">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-100 text-center mt-4 mb-4">Wallet Created Successfully!</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">Wallet Address:</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newWalletAddress}
                    readOnly
                    className="flex-1 px-3 py-2 border border-slate-600 rounded-md bg-slate-900 text-slate-200 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(newWalletAddress)}
                    className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                    title="Copy to clipboard"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => setNewWalletAddress(null)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-sm">
          <h3 className="text-lg font-medium text-slate-100 mb-4">Filter Transactions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className={inputClass}
              >
                <option value="">All Types</option>
                <option value="send_to_customer">Send to Customer</option>
                <option value="transfer_to_merchant">Transfer to Merchant</option>
                <option value="withdrawal">Withdrawal</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className={inputClass}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Amount</label>
              <input
                type="number"
                value={filters.amount}
                onChange={(e) => setFilters({ ...filters, amount: e.target.value })}
                placeholder="Exact amount"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
                placeholder="Transaction ID, wallet address..."
                className={inputClass}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-700 hover:bg-slate-600 rounded-md"
            >
              Clear Filters
            </button>
            <button
              onClick={applyFilters}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
            >
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Transactions List */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-700">
            {transactions.map((transaction) => (
              <li key={transaction.id} className="hover:bg-slate-700/40 transition-colors">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-500/15 flex items-center justify-center">
                          <CurrencyDollarIcon className="h-6 w-6 text-indigo-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-slate-100 font-mono">
                            {transaction.transactionId}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(transaction.type)}`}>
                            {transaction.type.replace(/_/g, ' ')}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                            {transaction.status}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-slate-400">
                          <span>Amount: {formatTokenAmount(transaction.amount)} {TOKEN_SYMBOL}</span>
                          <span>Created: {formatDate(transaction.createdAt)}</span>
                        </div>
                        {transaction.signature && (
                          <div className="mt-1 flex items-center space-x-2 text-sm text-slate-400">
                            <span>Signature:</span>
                            <code className="bg-slate-900 text-slate-200 px-2 py-1 rounded text-xs font-mono">
                              {transaction.signature.substring(0, 20)}...{transaction.signature.slice(-20)}
                            </code>
                            <button
                              onClick={() => navigator.clipboard.writeText(transaction.signature || '')}
                              className="inline-flex items-center px-2 py-1 text-xs text-indigo-400 hover:text-indigo-300"
                              title="Copy signature"
                            >
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            </button>
                            <a
                              href={`https://explorer.solana.com/tx/${transaction.signature}?cluster=devnet`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-2 py-1 text-xs text-blue-400 hover:text-blue-300"
                              title="View on Solana Explorer"
                            >
                              Explorer
                            </a>
                          </div>
                        )}
                        {transaction.errorMessage && (
                          <div className="mt-1 text-sm text-red-400">
                            Error: {transaction.errorMessage}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/transactions/${transaction.id}`}
                        className="inline-flex items-center px-3 py-1 border border-slate-600 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </Link>
                      {transaction.status === 'failed' && (
                        <>
                          <button
                            onClick={() => handleRepairAndRetry(transaction)}
                            disabled={
                              repairing === transaction.transactionId ||
                              retrying === transaction.transactionId
                            }
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 disabled:opacity-50"
                            title="Fund SOL, create token account if needed, then retry"
                          >
                            <WrenchScrewdriverIcon
                              className={`h-4 w-4 mr-1 ${
                                repairing === transaction.transactionId ? 'animate-spin' : ''
                              }`}
                            />
                            {repairing === transaction.transactionId ||
                            retrying === transaction.transactionId
                              ? 'Working...'
                              : 'Repair & Retry'}
                          </button>
                          <button
                            onClick={() => handleRetry(transaction.transactionId)}
                            disabled={retrying === transaction.transactionId}
                            className="inline-flex items-center px-3 py-1 border border-slate-600 text-sm leading-4 font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 disabled:opacity-50"
                          >
                            <ArrowPathIcon
                              className={`h-4 w-4 mr-1 ${
                                retrying === transaction.transactionId ? 'animate-spin' : ''
                              }`}
                            />
                            Retry
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {!loading && transactions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-400">No transactions found</p>
          </div>
        )}
      </div>

      {!loading && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
};

export default TransactionList;
